import { lighten, adaptV4Theme } from '@mui/material/styles';
import { createTheme, darken } from '@mui/material';
import { opacity } from './utils';

export const common = {
  black: '#0b0b0c',
  white: '#ffffff',
  background: '#FFFFFF',
  red: '#ff0000',
  increase: '#BEF2D7',
  decrease: '#F9CDBA',
  border: 'rgba(0,0,0,0.28)',
  grey: {
    50: '#FAFAFC',
    100: '#F5F5F7',
    200: '#EEEEF2',
    300: '#E0E0E0', // #DEDEE2',
    400: '#BDBDC2',
    500: '#9E9EA8',
    600: '#757575',
    700: '#757575',
    800: '#424247',
    900: '#212126',
  },
  acai: {
    1: '#fdfdfd', // color1 - Tangelo/Acai/acai1
    2: '#fbfbfc', // color2 - Tangelo/Acai/acai2
    3: '#f5f4f9', // color3 - Tangelo/Acai/acai3
    4: '#ebe8f3', // color4 - Tangelo/Acai/acai4
    5: '#e1dded', // color5 - Tangelo/Acai/acai5
    6: '#d6d2e7', // color6 - Tangelo/Acai/acai6
    7: '#c2bcdb', // color7 - Tangelo/Acai/acai7
    8: '#a69eca', // color8 - Tangelo/Acai/acai8
    9: '#6b5da8', // color9 - Tangelo/Acai/acai9
    10: '#6354a3', // color10 - Tangelo/Acai/acai10
    11: '#58489a', // color11 - Tangelo/Acai/acai11
    12: '#1c124d', // color12 - Tangelo/Acai/acai12
  },
  primaryColor: '#2972e0',
  secondaryColor: '#2972e0',
};

const palette = (
  primaryColor = '#2972e0',
  secondaryColor = '#2972e0',
  partnerTheme = null,
  tertiaryColor = null,
  borderWidth = null,
  borderRadius = null,
  borderColor = null,
  boxShadow = null,
) => {
  let background_color,
    border_color,
    border_radius,
    border_width,
    box_shadow,
    font_family,
    primary_color,
    secondary_color,
    tertiary_color,
    text_color;

  if (partnerTheme) {
    //console.log('using partnertheme in light.js')
    primaryColor = partnerTheme.primary_color != null ? partnerTheme.primary_color : primaryColor;
    secondaryColor = partnerTheme.secondary_color != null ? partnerTheme.secondary_color : secondaryColor;
    background_color = partnerTheme.background_color != null ? partnerTheme.background_color : '';
    border_color = partnerTheme.border_color != null ? partnerTheme.border_color : '';
    border_radius = partnerTheme.border_radius != null ? partnerTheme.border_radius : '';
    border_width = partnerTheme.border_width != null ? partnerTheme.border_width : '';
    box_shadow = partnerTheme.box_shadow != null ? partnerTheme.box_shadow : '';
    font_family = partnerTheme.font_family != null ? partnerTheme.font_family : '';
  }
  if (tertiaryColor) {
    tertiary_color = tertiaryColor;
  }
  if (borderWidth) {
    border_width = borderWidth;
  }
  if (borderRadius) {
    border_radius = borderRadius;
  }
  if (borderColor) {
    border_color = borderColor;
  }
  if (boxShadow) {
    box_shadow = boxShadow;
  }

  return {
    common: common,
    primary: {
      main: primaryColor != null ? primaryColor : common.primaryColor,
      dark: darken(primaryColor, 0.1),
      light: lighten(primaryColor, 0.3),
      contrastText: '#ffffff',
      background: lighten(primaryColor, 0.92),
      backgroundHover: lighten(primaryColor, 0.5),
      border: primaryColor,
      ...(partnerTheme?.primary_color && { primary_color: partnerTheme.primary_color }),
      ...(partnerTheme?.background_color && { background_color: partnerTheme.background_color }),
      ...(partnerTheme?.border_color && { border_color: partnerTheme.border_color }),
      ...(partnerTheme?.border_radius && { border_radius: partnerTheme.border_radius }),
      ...(partnerTheme?.border_width && { border_width: partnerTheme.border_width }),
      ...(partnerTheme?.box_shadow && { box_shadow: partnerTheme.box_shadow }),
      ...(partnerTheme?.font_family && { font_family: partnerTheme.font_family }),
    },
    black30: 'rgba(0, 0, 0, 0.30)',
    black60: 'rgba(0, 0, 0, 0.60)',
    black87: 'rgba(0, 0, 0, 0.87)',
    black12: 'rgba(0, 0, 0, 0.12)',
    border: {
      color: border_color || 'black',
      width: border_width || '1px',
      radius: border_radius || '4px',
    },
    secondary: {
      main: secondaryColor != null ? secondaryColor : common.secondaryColor,
      dark: darken(secondaryColor, 0.1),
      light: lighten(secondaryColor, 0.3),
      contrastText: '#ffffff',
      background: lighten(secondaryColor, 0.92),
      backgroundHover: lighten(secondaryColor, 0.5),
      border: secondaryColor,
      ...(partnerTheme?.secondary_color && { secondary_color: partnerTheme.secondary_color }),
    },
    tertiary: {
      main: tertiary_color || 'black',
    },
    alert: {
      infoColor: '#014361',
      infoBackground: '#F6F5FA',
    },
    toolTip: {
      active: lighten('#000000', 0.54),
      label: {
        color: '#FFFFFF',
        fontSize: '10px',
        fontWeight: 500,
        lineHeight: '14px',
      },
    },
    inputText: {
      color: '#7C7C7C',
    },
    // TODO: combine with success and error below
    successColors: {
      main: '#1B5E20',
      dark: '#1B5E20',
      hover: lighten('#2E7D32', 0.04),
      light: '#4CAF50',
    },
    errorColors: {
      main: '#D32F2F',
      dark: '#C62828',
      hover: lighten('#D32F2F', 0.04),
    },
    admin: {
      main: '#211551',
      dark: darken('#211551'),
      light: lighten('#211551'),
      bodyText: darken('#211551'),
      contrastText: lighten('#211551', 0.9),
      // background: lighten("#211551", 0.95),
      background: '#FFFFFF',
      backgroundHover: lighten('#211551', 0.6),
      border: '#211551', // lighten('#211551'),
      tab: lighten('#211551', 0.9),
      activeTab: lighten('#FFFFFF', 0.9),
      trendChart: lighten('#211551', 0.9),
      starter: lighten('#211551', 0.9),
      premium: lighten('#211551', 0.8),
      enterprise: lighten('#211551', 0.7),
      recommended: '#e2a787',
      greysBlack: '#1B1B1B',
      primaryLight: '#271869',
      avatarBackground: '#06A88D',
    },
    error: {
      main: '#DA4045',
      dark: '#A74145',
      light: '#EBB1B3',
      contrastText: '#ffffff',
      background: lighten('#DA4045', 0.92),
      backgroundHover: lighten('#DA4045', 0.5),
      border: '#A74145',
    },
    warning: {
      main: '#f8ab3a',
      dark: '#c38147',
      light: '#ffc960',
      contrastText: '#ffffff',
      background: lighten('#f8ab3a', 0.92),
      backgroundHover: lighten('#f8ab3a', 0.5),
      border: '#c38147',
    },
    success: {
      main: '#45ae57',
      dark: '#3d954c',
      light: '#60ba6f',
      contrastText: '#ffffff',
      background: lighten('#68b76b', 0.92),
      backgroundHover: lighten('#68b76b', 0.5),
      border: '#3d954c',
    },
    info: {
      main: '#536dfe',
      dark: '#25448f',
      light: '#9fa8da',
      contrastText: '#ffffff',
      background: lighten('#536dfe', 0.92),
      backgroundHover: lighten('#536dfe', 0.5),
      border: '#25448f',
    },
    border: {
      color: border_color || 'black',
      width: border_width || '1px',
      radius: border_radius || '4px',
    },
    boxShadow: box_shadow || 'none',
    background: {
      paper: '#ffffff',
      // default: '#fafafa',
      default: '#FFFFFF',
    },
  };
};

const theme = (
  primaryColor,
  secondaryColor,
  partnerTheme = null,
  tertiaryColor = null,
  borderWidth = null,
  borderRadius = null,
  borderColor = null,
  boxShadow = null,
) => {
  // console.log('here is borderColor in light.js????: ', borderColor);

  const finalPalette = palette(
    primaryColor,
    secondaryColor,
    partnerTheme,
    tertiaryColor,
    borderWidth,
    borderRadius,
    borderColor,
    boxShadow,
  );

  return createTheme(
    adaptV4Theme({
      themeName: 'Light',
      palette: finalPalette,
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: finalPalette.background.default,
              minHeight: '100vh',
              width: '100vw',
              margin: 0,
              padding: 0,
              overflowX: 'hidden', // Prevent horizontal scrolling
              fontFamily: 'Montserrat',
              color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
            },
            '#root': {
              minHeight: '100vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden', // Ensure no horizontal overflow
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              borderRadius: 'var(--borderRadius, 8px)',
              background: 'var(--_components-tooltip-fill, rgba(97, 97, 97, 0.90))',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              maxWidth: '200px',
            },
            arrow: {
              color: 'var(--_components-tooltip-fill, rgba(97, 97, 97, 0.90))',
            },
          },
        },
      },
      globalClasses: {
        screen: {
          width: '96%',
          margin: '10px auto auto',
        },
        adminTableHead: {
          borderRadius: '12px',
          borderCollapse: 'separate',
          '& .MuiTableRow-root': {
            '& .MuiTableCell-root:first-child': {
              borderTopLeftRadius: '12px',
            },
            '& .MuiTableCell-root:last-child': {
              borderTopRightRadius: '12px',
            },
          },
          backgroundColor: `rgba(0, 0, 0, 0.08)`,
          '& *': {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '18px',
          },
        },
        adminTableIcon: {
          margin: 0,
          color: finalPalette.admin.main,
          width: 18,
          height: 18,
        },
        infoRoundedIcon: {
          color: finalPalette.black30,
          width: 20,
          height: 20,
          marginRight: '10px',
          verticalAlign: 'bottom',
        },
        adminTableWrapper: {
          border: 'none',
          borderRadius: 0,
          overflow: 'visible',
          width: '100%',
          height: 'auto',
          boxShadow: 'none',
          cursor: 'default',
          padding: 0,
          margin: 0,
        },
        adminTableRowWrapper: {
          cursor: 'default',
          fontSize: 14,
          fontWeight: 400,
        },
        adminTableRow: {
          borderRadius: 5,
          color: finalPalette.admin.bodyText,
          backgroundColor: finalPalette.admin.trendChart,
          '&:nth-of-type(odd)': {
            backgroundColor: darken(finalPalette.admin.trendChart, 0.05),
          },
          '&:nth-of-type(even)': {
            backgroundColor: finalPalette.admin.trendChart,
          },
          // '& td:first-child': {
          //   borderRight: `2px solid ${darken(finalPalette.admin.main, 0.3)}`,
          // },
          '& *': {
            color: finalPalette.admin.bodyText,
          },
          '& .MuiTableSortLabel-active': {
            color: finalPalette.admin.dark,
          },
        },
        adminTableRowWhite: {
          borderRadius: 5,
          color: finalPalette.admin.bodyText,
          backgroundColor: finalPalette.common.white,
          '& *': {
            color: finalPalette.admin.bodyText,
          },
          '& .MuiTableSortLabel-active': {
            color: finalPalette.admin.dark,
          },
        },
        patientChartCard: {
          cursor: 'pointer',
          color: finalPalette.common.grey['600'],
          fontFamily: 'Montserrat',
          padding: createTheme().spacing(1),
          width: '100%',
          height: 'auto',
          '& a': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
            textDecoration: 'underline',
            textDecorationStyle: 'solid',
            textDecorationSkipInk: 'auto',
            textDecorationThickness: 'auto',
            cursor: 'pointer',
          },
          '& textarea': {
            borderRadius: 5,
            padding: 5,
          },
        },
        frame: {
          display: 'flex',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '10px',
          flex: '1 0 0',
          borderRadius: '12px',
          border: '1px solid var(--divider, rgba(0, 0, 0, 0.12))',
          background: 'var(--primary-contrast, #FFF)',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        },
        inputFrame: {
          display: 'flex',
          padding: '6px 16px',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
          borderRadius: 'var(--borderRadius, 8px)',
          border: `1px solid ${finalPalette.admin.infoMain}`,
        },
        chipSuccess: {
          borderRadius: '100px',
          border: '1px solid #1B5E20',
          background: 'rgba(46, 125, 50, 0.04)',
          color: 'var(--success-dark, #1B5E20)',
        },
        chipError: {
          borderRadius: '100px',
          border: '1px solid var(--error-main, #D32F2F)',
          background: 'var(--error-_states-hover, rgba(211, 47, 47, 0.08))',
          color: 'var(--error-error-dark, #C62828)',
        },
        chipDefault: {
          borderRadius: '100px',
          border: '1px solid var(--_components-chip-defaultEnabledBorder, #BDBDBD)',
          background: 'var(--action-hover, rgba(0, 0, 0, 0.04))',
          color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
        },
        unitRegular: {
          fontSize: '14px',
          color: finalPalette.black87,
        },
        unitBold: {
          fontSize: '14px',
          color: finalPalette.black87,
          fontWeight: 600,
        },
        generalStepContainer: {
          display: 'flex',
          justifyContent: 'center',
        },
        assessmentContainer: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          margin: 0,
          padding: 0,
          width: 'clamp(300px, 50vw, 500px)',
        },
        assessmentButton: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          zIndex: 1000,
          minWidth: '140px',
          maxWidth: '278px',
          marginTop: 2,
          // Button styles that will be applied by referencing this class in your button
          '& .MuiButton-root': {
            padding: '8px 22px',
            borderRadius: 'var(--borderRadius, 8px)',
            fontSize: '15px',
            fontWeight: 700,
          },
        },
        questionBoxAbstract: {
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: '14px',
          border: '1px solid #C3C3C3',
          background: '#FFF',
        },
        questionBoxContainer: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: '278px',
          width: 'clamp(300px, 50vw, 500px)', // 600
        },
        questionBoxHeaderContainer: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px',
          alignSelf: 'stretch',
        },
        questionText: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignSelf: 'stretch',
          color: finalPalette.black87,
          textAlign: 'center',
          fontFeatureSettings: 'liga off, clig off',
          fontSize: '24px !important',
          fontStyle: 'normal',
          fontWeight: '700',
          lineHeight: '125%',
          margin: 0,
          '&.MuiTypography-root': {
            fontSize: '24px !important',
            fontWeight: '700',
            lineHeight: '125%',
          },
        },
        descriptionText: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignSelf: 'stretch',
          color: finalPalette.black87,
          textAlign: 'center',
          fontFeatureSettings: 'liga off, clig off',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
          letterSpacing: '0.15px',
        },
        instructionsText: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignSelf: 'stretch',
          color: `var(--text-secondary, ${finalPalette.black60})`,
          textAlign: 'center',
          fontFeatureSettings: 'liga off, clig off',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '500',
          lineHeight: '125%',
          letterSpacing: '0.1px',
        },
        questionBoxOptionsContainer: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '32px',
          alignSelf: 'stretch',
        },
        questionBoxOptions: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          alignSelf: 'stretch',
        },
        questionsList: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '12px',
          alignSelf: 'stretch',
        },
        questionInputContainer: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
        },
        questionInputLabel: {
          color: `var(--text-secondary, ${finalPalette.black60})`,
        },
        inputContainer: {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          marginTop: '16px',
        },
        textField: {
          width: '100%',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        },
        questionBoxAbstractContainer: {
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: 'clamp(300px, 50vw, 500px)', // 600px
        },
        questionBoxAbstractSelectionContainer: {
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        },
      },
      typography: {
        useNextVariants: true,
        htmlFontSize: 16,
        fontFamily: 'Montserrat',
        fontSize: 14,
        fontWeightLight: 400,
        fontWeightRegular: 400,
        fontWeightMedium: 400,
        fontWeightBold: 400,
        h1: {
          color: 'var(--Greys-Black, #1B1B1B)',
          fontSize: '32px',
          fontStyle: 'normal',
          fontWeight: 800,
          lineHeight: '125%',
          letterSpacing: '1px',
        },
        h2: {
          color: `var(--text-primary, ${finalPalette.black87})`,
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: '125%',
          letterSpacing: '1px',
        },
        h3: {
          color: finalPalette.admin.main,
          fontSize: '18.72px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: '116.7%',
        },
        h4: {
          fontWeight: 600,
        },
        h5: {},
        h6: {},
        body1: {
          color: finalPalette.admin.bodyText,
          '& b': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
          },
        },
        body2: {
          color: finalPalette.admin.bodyText,
        },
        subtitle1: {
          fontWeight: 600,
          width: '100%',
          marginBottom: 100,
          fontSize: 24,
          color: finalPalette.admin.main,
        },
        helper: {
          primary: finalPalette.common.grey['100'],
          secondary: opacity(finalPalette.common.grey['100'], 0.54),
          light: '#DFE9E9',
        },
        subtitle: {
          margin: 0,
          fontSize: 24,
          fontWeight: 700,
          lineHeight: '125%',
          letterSpacing: '0.15px',
        },
        subtitle2: {
          // was undefined; this is subtitle1 in figma
          margin: 0,
          fontSize: 16,
          fontWeight: 400,
          lineHeight: '125%',
          letterSpacing: '0.15px',
        },
        summaryTitle: {
          margin: 0,
          color: finalPalette.admin.secondary,
          fontSize: 12,
          fontWeight: 400,
          letterSpacing: '1px',
          lineHeight: '266%',
          textTransform: 'uppercase',
        },
        tableHeadLink: {
          color: finalPalette.admin.main,
          fontSize: 13,
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: '22px',
          letterSpacing: '0.46px',
          textTransform: 'uppercase',
          // paddingBottom: '10px',
          height: '24px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
        },
        alertTitle: {
          color: finalPalette.alert.infoColor,
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '150%',
          letterSpacing: '0.15px',
        },
        overline: {
          color: 'var(--text-secondary, rgba(0, 0, 0, 0.60))',
          fontSize: 13,
          fontWeight: 400,
          height: '24px',
          lineHeight: '24px',
          letterSpacing: '0.46px',
          textTransform: 'uppercase',
          display: 'flex',
          paddingBottom: '12px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignSelf: 'stretch',
        },
        assessmentSectionHeader: {
          color: `var(--color11, ${finalPalette.common.acai[11]})`,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: '125%',
          textAlign: 'center',
          width: '100%',
        },
        assessmentSectionTitle: {
          textAlign: 'center',
          width: '100%',
        },
        assessmentSectionSelection: {
          color: `var(--color12, ${finalPalette.common.acai[12]})`,
          fontSize: 16,
          fontWeight: 400,
          lineHeight: 'normal',
          textAlign: 'center',
        },
        assessmentSubtitle1: {
          color: `var(--text-secondary, ${finalPalette.black87})`,
          textAlign: 'center',
          alignSelf: 'stretch',
          fontSize: 16,
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '125%',
          letterSpacing: '0.15px',
        },
        assessmentSubtitle2: {
          color: `var(--text-secondary, ${finalPalette.black60})`,
          fontSize: 14,
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '125%',
          letterSpacing: '0.1px',
          textAlign: 'center',
          alignSelf: 'stretch',
          marginBottom: '16px',
        },
      },
      overrides: {
        MuiButton: {
          root: {},
          contained: {},
          outlined: {},
          text: {},
          sizeSmall: {},
          sizeLarge: {},
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
        // MuiTextField: {
        //   root: {
        //     borderRadius: finalPalette.border.radius,
        //   },
        //   contained: {},
        //   outlined: {},
        //   text: {},
        //   sizeSmall: {},
        //   sizeLarge: {},
        // },
      },
      breakpoints: {
        values: {
          xs: 320,
          sm: 641,
          md: 960,
          lg: 1080,
          xl: 1920,
        },
      },
    }),
  );
};

export default theme;
